import { t } from "@lingui/macro";
import { Dialog, DialogContent } from "@reactive-resume/ui";
import type { LinkedInProfileDraft } from "@reactive-resume/dto";
import { LINKEDIN_URL_RE, LinkedInProfileDraftSchema } from "@reactive-resume/dto";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";

import { useToast } from "@/client/hooks/use-toast";
import {
  pollLinkedInImport,
  startLinkedInImport,
  useConfirmLinkedInImport,
} from "@/client/services/linkedin/api";
import { useDialog } from "@/client/stores/dialog";

import { ImportFlowShell } from "./import-flow/components/import-flow-shell";
import { ImportSuccessStep } from "./import-flow/steps/import-success-step";
import { LinkedInPreviewStep } from "./import-flow/steps/linkedin-preview-step";
import { LinkedInProgressStep } from "./import-flow/steps/linkedin-progress-step";
import { LinkedInUrlStep } from "./import-flow/steps/linkedin-url-step";

type LinkedInImportStep = "url" | "importing" | "preview" | "success";

const POLL_INTERVAL_MS = 2_500;

const POLL_TIMEOUT_MS = 120_000;

const ASSETS = {
  url: "/images/user-space/onboarding/import-document/import-guide-upload.webp",
  importing: "/images/user-space/onboarding/import-document/import-guide-upload.webp",
  preview: "/images/user-space/onboarding/import-document/import-guide-sections.webp",
  success: "/images/user-space/onboarding/import-document/guide-template.webp",
} satisfies Record<LinkedInImportStep, string>;

const MESSAGES = {
  url: "Importez votre profil LinkedIn, je m’occupe du reste.",
  importing: "Je récupère et j’organise vos informations.",
  preview: "Vérifiez les informations avant de créer votre CV.",
  success: "Votre nouveau CV est prêt à être personnalisé !",
} satisfies Record<LinkedInImportStep, string>;

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const errorData = error as {
    response?: {
      data?: {
        message?: string | string[];
      };
    };
    message?: string;
  };

  const apiMessage = errorData.response?.data?.message;

  if (Array.isArray(apiMessage)) {
    return apiMessage.join(" — ");
  }

  return apiMessage ?? errorData.message ?? fallback;
};

export const ImportLinkedinDialog = () => {
  const { isOpen, close } = useDialog("import-linkedin");

  const navigate = useNavigate();

  const { toast } = useToast();

  const confirmImport = useConfirmLinkedInImport();

  const [step, setStep] = useState<LinkedInImportStep>("url");

  const [url, setUrl] = useState("");

  const [consent, setConsent] = useState(false);

  const [starting, setStarting] = useState(false);

  const [importError, setImportError] = useState("");

  const [jobId, setJobId] = useState<string | null>(null);

  const [elapsed, setElapsed] = useState(0);

  const [draft, setDraft] = useState<LinkedInProfileDraft | null>(null);

  const [resumeId, setResumeId] = useState<string | null>(null);

  const urlValid = useMemo(() => LINKEDIN_URL_RE.test(url.trim()), [url]);

  const canSubmit = urlValid && consent && !starting;

  const reset = useCallback(() => {
    setStep("url");
    setUrl("");
    setConsent(false);
    setStarting(false);
    setImportError("");
    setJobId(null);
    setElapsed(0);
    setDraft(null);
    setResumeId(null);

    confirmImport.reset();
  }, [confirmImport]);

  const closeDialog = useCallback(() => {
    reset();
    close();
  }, [reset, close]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeDialog();
    }
  };

  const failImport = useCallback(
    (message: string) => {
      setImportError(message);

      setStep("url");
      setJobId(null);
      setElapsed(0);

      toast({
        variant: "destructive",
        title: "Import impossible",
        description: message,
      });
    },
    [toast],
  );

  /*
   * Horloge utilisée uniquement
   * pour l’animation de progression.
   *
   * Elle ne remplace pas la progression
   * réelle renvoyée par le backend.
   */
  useEffect(() => {
    if (step !== "importing") {
      return;
    }

    const startedAt = Date.now();

    setElapsed(0);

    const timer = window.setInterval(() => {
      setElapsed(Date.now() - startedAt);
    }, 1_000);

    return () => {
      window.clearInterval(timer);
    };
  }, [step]);

  /*
   * Polling du job LinkedIn.
   *
   * Le contrat backend reste :
   * POST /linkedin/import/poll
   * avec le jobId reçu au démarrage.
   */
  useEffect(() => {
    if (step !== "importing" || !jobId) {
      return;
    }

    let cancelled = false;

    let timer: number | undefined;

    const startedAt = Date.now();

    const poll = async () => {
      if (cancelled) {
        return;
      }

      try {
        const status = await pollLinkedInImport(jobId);

        if (cancelled) {
          return;
        }

        if (status.ready) {
          if (status.status === "DONE" && status.draft != null) {
            const parsed = LinkedInProfileDraftSchema.safeParse(status.draft);

            if (parsed.success) {
              setDraft(parsed.data);

              setStep("preview");

              return;
            }

            failImport(
              "Les données reçues ont une structure inattendue. Réessayez dans un instant.",
            );

            return;
          }

          failImport(
            status.error ??
              "L’extraction a échoué. Vérifiez que votre profil est public puis réessayez.",
          );

          return;
        }

        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          failImport(
            t`L’extraction prend trop de temps. Réessayez dans quelques minutes ou utilisez l’import PDF (.pdf).`,
          );

          return;
        }

        timer = window.setTimeout(() => {
          void poll();
        }, POLL_INTERVAL_MS);
      } catch (error) {
        if (cancelled) {
          return;
        }

        failImport(
          getApiErrorMessage(error, "La connexion au service d’import a échoué. Réessayez."),
        );
      }
    };

    timer = window.setTimeout(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;

      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [step, jobId, failImport]);

  const submitUrl = async (event: FormEvent) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    setStarting(true);
    setImportError("");

    try {
      /*
       * Backend existant :
       * POST /linkedin/import/url
       */
      const result = await startLinkedInImport(url.trim(), consent);

      setJobId(result.jobId);

      setElapsed(0);
      setStep("importing");
    } catch (error) {
      const message = getApiErrorMessage(error, "Impossible de démarrer l’import LinkedIn.");

      setImportError(message);

      toast({
        variant: "destructive",
        title: "Import impossible",
        description: message,
      });
    } finally {
      setStarting(false);
    }
  };

  const retryImport = () => {
    setStep("url");
    setJobId(null);
    setElapsed(0);
    setDraft(null);
    setImportError("");
  };

  const confirmDraft = async () => {
    if (!jobId || confirmImport.isPending) {
      return;
    }

    try {
      /*
       * Backend existant :
       * POST /linkedin/import/confirm
       *
       * Le serveur crée le CV et
       * retourne son resumeId.
       */
      const result = await confirmImport.mutateAsync(jobId);

      setResumeId(result.resumeId);

      setStep("success");

      toast({
        variant: "success",
        title: "CV créé avec vos informations LinkedIn",
        description: result.duplicate
          ? "Un nouveau brouillon a été créé. Votre ancien CV reste disponible."
          : "Vérifiez maintenant chaque section dans le builder.",
      });
    } catch (error) {
      const message = getApiErrorMessage(error, "La création du CV a échoué. Réessayez.");

      toast({
        variant: "destructive",
        title: "Création impossible",
        description: message,
      });
    }
  };

  const openBuilder = () => {
    if (!resumeId) {
      return;
    }

    const nextUrl = `/client/builder/${resumeId}`;

    closeDialog();

    void navigate(nextUrl);
  };

  const openDocuments = () => {
    closeDialog();

    void navigate("/client/user-space/resumes");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="h-[100dvh] w-screen max-w-none overflow-hidden rounded-none border-0 p-0 sm:h-[min(94dvh,900px)] sm:w-[calc(100vw-2rem)] sm:rounded-[1.75rem] lg:max-w-[1500px] [&>button]:hidden">
        <ImportFlowShell
          illustration={ASSETS[step]}
          illustrationAlt="Assistant MonCVPro pour l’import LinkedIn"
          message={MESSAGES[step]}
          onClose={closeDialog}
        >
          {step === "url" && (
            <LinkedInUrlStep
              url={url}
              consent={consent}
              urlValid={urlValid}
              busy={starting}
              error={importError}
              onUrlChange={(value) => {
                setUrl(value);

                if (importError) {
                  setImportError("");
                }
              }}
              onConsentChange={setConsent}
              onSubmit={submitUrl}
            />
          )}

          {step === "importing" && <LinkedInProgressStep elapsedMilliseconds={elapsed} />}

          {step === "preview" && draft && (
            <LinkedInPreviewStep
              draft={draft}
              pending={confirmImport.isPending}
              onConfirm={() => {
                void confirmDraft();
              }}
              onRetry={retryImport}
            />
          )}

          {step === "success" && (
            <ImportSuccessStep
              source="linkedin"
              onOpenBuilder={openBuilder}
              onOpenDocuments={openDocuments}
            />
          )}
        </ImportFlowShell>
      </DialogContent>
    </Dialog>
  );
};
